import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { TextField, InputAdornment } from "@mui/material";
import { HiMagnifyingGlass } from "react-icons/hi2";
import FastAPIClient from "../../client";
import config from "../../config";
import { useNetwork } from "../NetworkProvider";
import SearchLoadSkeleton from "../Skeletons/SearchLoadSkeleton";

const client = new FastAPIClient(config);

// Add string similarity function
const stringSimilarity = (str1, str2) => {
    str1 = str1.toLowerCase();
    str2 = str2.toLowerCase();

    // If strings are identical, return 1
    if (str1 === str2) return 1;

    // If one string is empty, return 0
    if (!str1 || !str2) return 0;

    // Calculate Levenshtein distance
    const matrix = [];
    for (let i = 0; i <= str1.length; i++) {
        matrix[i] = [i];
    }
    for (let j = 0; j <= str2.length; j++) {
        matrix[0][j] = j;
    }

    for (let i = 1; i <= str1.length; i++) {
        for (let j = 1; j <= str2.length; j++) {
            if (str1[i - 1] === str2[j - 1]) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j] + 1
                );
            }
        }
    }

    // Calculate similarity score
    const maxLength = Math.max(str1.length, str2.length);
    return 1 - matrix[str1.length][str2.length] / maxLength;
};

const ClientSearch = ({ allClients, user_id }) => {
    const { isOnline } = useNetwork();
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredClients, setFilteredClients] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const nav = useNavigate();
    const searchRef = useRef(null);

    // Add debounce delay constant
    const DEBOUNCE_DELAY = 300; // 300ms delay

    // Add click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const calculateMatchScore = (searchTerm, client) => {
        const clientName = client.client_name.toLowerCase();
        const clientFullName = client.client_name_full.toString().toLowerCase();
        const clientCity = client.city.toLowerCase();

        // Split search term into words
        const searchWords = searchTerm.toLowerCase().split(/\s+/);

        // Initialize scores
        let totalScore = 0;
        let matchedWords = 0;

        // Weights for different types of matches
        const weights = {
            exactMatch: 1.0,
            highSimilarity: 0.8,
            mediumSimilarity: 0.6,
            partialMatch: 0.4,
            singleWordMatch: 0.5, // New weight for single word matches
        };

        // Check each search word
        for (const word of searchWords) {
            let wordScore = 0;

            // Check exact matches first
            if (
                clientName === word ||
                clientFullName === word ||
                clientCity === word
            ) {
                wordScore = weights.exactMatch;
            } else {
                // Calculate similarities
                const nameSimilarity = stringSimilarity(word, clientName);
                const fullNameSimilarity = stringSimilarity(
                    word,
                    clientFullName
                );
                const citySimilarity = stringSimilarity(word, clientCity);

                // For single word searches, be more lenient
                if (searchWords.length === 1) {
                    // Check if the word is part of any field
                    if (
                        clientName.includes(word) ||
                        clientFullName.includes(word) ||
                        clientCity.includes(word)
                    ) {
                        wordScore = weights.singleWordMatch;
                    }
                    // Check for high similarity matches
                    else if (
                        nameSimilarity > 0.7 ||
                        fullNameSimilarity > 0.7 ||
                        citySimilarity > 0.7
                    ) {
                        wordScore = weights.highSimilarity;
                    }
                } else {
                    // For multi-word searches, use stricter matching
                    if (
                        nameSimilarity > 0.8 ||
                        fullNameSimilarity > 0.8 ||
                        citySimilarity > 0.8
                    ) {
                        wordScore = weights.highSimilarity;
                    } else if (
                        nameSimilarity > 0.7 ||
                        fullNameSimilarity > 0.7 ||
                        citySimilarity > 0.7
                    ) {
                        wordScore = weights.mediumSimilarity;
                    } else if (
                        clientName.includes(word) ||
                        clientFullName.includes(word) ||
                        clientCity.includes(word) ||
                        word.includes(clientName) ||
                        word.includes(clientFullName) ||
                        word.includes(clientCity)
                    ) {
                        wordScore = weights.partialMatch;
                    }
                }
            }

            // Only count words that have some match
            if (wordScore > 0) {
                totalScore += wordScore;
                matchedWords++;
            }
        }

        // For single word searches, be more lenient with the minimum score
        const minimumScore = searchWords.length === 1 ? 0.4 : 0.6;

        // Calculate final score based on matched words ratio
        const matchRatio = matchedWords / searchWords.length;
        return {
            score: totalScore * matchRatio,
            matchedWords,
            minimumScore,
        };
    };

    const debouncedSearch = useCallback(
        async (term) => {
            if (term === "") {
                setShowSuggestions(false);
                setFilteredClients([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            if (isOnline) {
                try {
                    const data = await client.searchClients(user_id, term);
                    setFilteredClients(data);
                    setShowSuggestions(true);
                } catch (error) {
                    console.error("Error searching clients:", error);
                    setFilteredClients([]);
                } finally {
                    setLoading(false);
                }
            } else {
                const filtered = allClients
                    .filter((client) => client.is_active)
                    .filter((client) => {
                        const { score, matchedWords, minimumScore } =
                            calculateMatchScore(term, client);

                        // Require at least one word to be a close match
                        const hasCloseMatch = matchedWords > 0;

                        return hasCloseMatch && score >= minimumScore;
                    })
                    .sort((a, b) => {
                        // Sort by match score
                        const scoreA = calculateMatchScore(term, a).score;
                        const scoreB = calculateMatchScore(term, b).score;
                        return scoreB - scoreA;
                    });

                setFilteredClients(filtered);
                setShowSuggestions(true);
                setLoading(false);
            }
        },
        [isOnline, allClients, user_id]
    );

    // Replace the search term effect with a debounced version
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchTerm !== "") {
                debouncedSearch(searchTerm);
            }
        }, DEBOUNCE_DELAY);

        // Cleanup function to cancel the timeout if the component unmounts
        // or if searchTerm changes before the delay is finished
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, debouncedSearch]);

    const handleClientClick = (client) => {
        setSearchTerm("");
        setShowSuggestions(false);
        if (client.is_lead) {
            nav(`/lead/${client.client_id}`);
        } else {
            nav(`/client/${client.client_id}`);
        }
    };

    return (
        <div className="relative w-full h-full" ref={searchRef}>
            <FormattedMessage
                id="home.SearchClients"
                defaultMessage="Rechercher ..."
            >
                {(msg) => (
                    <div className="relative">
                        <TextField
                            fullWidth
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={() => {
                                if (searchTerm !== "") {
                                    setShowSuggestions(true);
                                }
                            }}
                            placeholder={msg}
                            variant="outlined"
                            size="small"
                            className="bg-white"
                            sx={{
                                "& .MuiInputBase-root": {
                                    backgroundColor: "#f3f4f6",
                                    borderRadius: "9999px",
                                    height: "40px",
                                    "@media (max-width: 768px)": {
                                        height: "48px",
                                    },
                                    "&:hover": {
                                        backgroundColor: "#f3f4f6",
                                    },
                                    "&.Mui-focused": {
                                        backgroundColor: "#f3f4f6",
                                    },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                    {
                                        border: "none",
                                    },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                        border: "none",
                                    },
                                "& .MuiInputBase-input": {
                                    padding: "8px 14px",
                                    fontSize: "0.875rem",
                                    "@media (max-width: 768px)": {
                                        fontSize: "1.125rem",
                                        padding: "12px 14px",
                                    },
                                },
                                "& .MuiInputAdornment-root": {
                                    "@media (max-width: 768px)": {
                                        marginLeft: "12px",
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <HiMagnifyingGlass
                                            className="text-gray-500"
                                            size={18}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                )}
            </FormattedMessage>
            {showSuggestions && searchTerm !== "" && (
                <div className="absolute top-full w-full rounded-lg max-h-60 z-10 mt-1">
                    <div className="w-full max-h-60 bg-white border border-slate-300 rounded-lg shadow-lg overflow-y-auto text-left">
                        {loading ? (
                            <SearchLoadSkeleton />
                        ) : filteredClients.length > 0 ? (
                            filteredClients.map((client) => (
                                <div
                                    key={client.client_id}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleClientClick(client);
                                    }}
                                    className="cursor-pointer px-3 py-2 hover:bg-slate-100 border-b border-gray-200"
                                >
                                    <div className="flex flex-row items-center justify-between">
                                        <div className="flex flex-col text-base text-black w-2/3">
                                            {client.client_name},{" "}
                                            <span className="italic text-sm text-gray-700">
                                                {client.city}
                                            </span>
                                        </div>
                                        <div className="text-base md:text-xs text-gray-700 w-1/3 text-right">
                                            ID:{client.client_id}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="px-3 py-2">
                                <FormattedMessage id="no-match" />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientSearch;
